import { TypedDocumentNode, gql } from '@apollo/client';
import { Navigate, generatePath } from 'react-router-dom';

import { useTypedParams } from '@sorare/routing';

import { So5LeaderboardSeasonality } from '@sorare/core/src/__generated__/globalTypes';
import { FOOTBALL_PLAY_PRO_FIXTURE } from '@sorare/core/src/constants/__generated__/routes';
import useFeatureFlags from '@sorare/core/src/hooks/useFeatureFlags';
import { withRouteQuery } from '@sorare/core/src/lib/navigation/withRouteQuery';

import { AddTeam } from '@sorare/gameplay/src/components/league/AddTeam';
import { CompetitionUnlockedWithCard } from '@sorare/gameplay/src/components/league/CompetitionUnlockedWithCard';
import { useComposeFlowFF } from '@sorare/gameplay/src/hooks/compose/useComposeFlowFF';
import {
  getAddTeamPath,
  getFixtureQueryVariables,
  getLeaguesWithContenders,
  isFixtureOpened,
} from '@sorare/gameplay/src/lib/so5';
import { PlayProFixturePage } from '@sorare/gameplay/src/pages/PlayProFixturePage';

import { ForYouTeams } from 'components/league/ForYouTeams';
import { LeagueTeams } from 'components/league/LeagueTeams';

import {
  FootballPlayProFixturePageQuery,
  FootballPlayProFixturePageQueryVariables,
} from './__generated__/page.graphql';
import { PageParams } from './__generated__/routeParams';
import matchCenterDesktopBg from './match_center_desktop_bg.jpg';
import matchCenterMobileBg from './match_center_mobile_bg.jpg';

const FOOTBALL_PLAY_PRO_FIXTURE_PAGE_QUERY = gql`
  query FootballPlayProFixturePageQuery($type: So5State, $slug: String) {
    so5 {
      nextSo5Fixture: so5Fixture(type: UPCOMING, sport: FOOTBALL) {
        slug
        ...PlayProFixturePage_nextSo5Fixture
      }
      so5Fixture(type: $type, slug: $slug, sport: FOOTBALL) {
        slug
        myMissedLineups {
          slug
        }
        so5Leagues {
          slug
          displayName
          headerUrl
          color
          mySo5LeaderboardContenders {
            slug
          }
          unlockedWithCard {
            ... on AnyCardInterface {
              slug
            }
          }
          ...AddTeam_so5LeaderboardGroupInterface
          ...CompetitionUnlockedWithCard_so5League
          ...getLeaguesWithContenders_so5LeaderboardGroupInterface
        }
        ...PlayProFixturePage_so5Fixture
        ...isFixtureOpened_so5Fixture
      }
    }
  }
  ${PlayProFixturePage.fragments.nextSo5Fixture}
  ${PlayProFixturePage.fragments.so5Fixture}
  ${isFixtureOpened.fragments.so5Fixture}
  ${AddTeam.fragments.so5LeaderboardGroupInterface}
  ${CompetitionUnlockedWithCard.fragments.so5League}
  ${getLeaguesWithContenders.fragments.so5LeadearboardGroupInterface}
` as TypedDocumentNode<
  FootballPlayProFixturePageQuery,
  FootballPlayProFixturePageQueryVariables
>;

const FootballPlayProFixturePage = withRouteQuery(
  ({ queryResult }) => {
    const {
      flags: { useForYouTeams = false },
    } = useFeatureFlags();
    const useComposeFlow20 = useComposeFlowFF();
    const { fixture } = useTypedParams<PageParams>();
    const { so5 } = queryResult.data;
    const { nextSo5Fixture, so5Fixture } = so5;

    if (!so5Fixture) return null;

    if (fixture !== so5Fixture.slug) {
      return (
        <Navigate
          to={generatePath(FOOTBALL_PLAY_PRO_FIXTURE, {
            fixture: so5Fixture.slug,
          })}
          replace
        />
      );
    }

    const isUpcoming = isFixtureOpened(so5Fixture);

    const {
      shouldDisplayCompetitionUnlockedWithCard,
      so5LeaderboardGroupsWithContenders: so5LeaguesWithContenders,
    } = getLeaguesWithContenders(so5Fixture.so5Leagues, isUpcoming);

    return (
      <PlayProFixturePage
        so5Fixture={so5Fixture}
        nextSo5Fixture={nextSo5Fixture}
        leagueTeamNodes={so5LeaguesWithContenders.map(so5League => (
          <LeagueTeams
            key={so5League.slug}
            slug={so5League.slug}
            mySo5LeaderboardContenders={so5League.mySo5LeaderboardContenders}
            leagueLogo={so5League.headerUrl}
            leagueName={so5League.displayName}
            leagueColor={so5League.color}
          >
            {so5League.unlockedWithCard &&
            shouldDisplayCompetitionUnlockedWithCard ? (
              <CompetitionUnlockedWithCard
                so5League={so5League}
                to={getAddTeamPath({
                  so5LeaderboardGroupInterface: so5League,
                  seasonality: So5LeaderboardSeasonality.ALL_SEASONS,
                  useComposeFlow20,
                })}
              />
            ) : (
              <AddTeam so5LeaderboardGroupInterface={so5League} />
            )}
          </LeagueTeams>
        ))}
        matchCenterBgImage={{
          desktop: matchCenterDesktopBg,
          mobile: matchCenterMobileBg,
        }}
        extra={
          useForYouTeams && (
            <ForYouTeams
              slug={so5Fixture.slug}
              myMissedLineupsCount={so5Fixture.myMissedLineups.length}
            />
          )
        }
      />
    );
  },
  {
    query: FOOTBALL_PLAY_PRO_FIXTURE_PAGE_QUERY,
    queryOptions: ({ fixture }: PageParams) => ({
      variables: getFixtureQueryVariables(fixture),
    }),
    preventTransitionBeforeLoaded: true,
  }
);

export default FootballPlayProFixturePage;
